<template>
    <div ref="appContent" id="app-content">
        <navigation-menu />

        <div id="main-account">
            <account-header :title="pageTitle" />

            <div id="club-info-small-resolution" class="d-block d-lg-none px-3 py-2">
                <selected-club-info />
            </div>

            <div class="page-title__small-resolution d-block d-lg-none">
                <span v-if="!shouldSeeAthleteDropdown" class="d-block page__title px-3 py-2">{{ formattedTitle }}</span>
                <header-profile-coach v-if="shouldSeeAthleteDropdown" class="d-block" />
            </div>

            <router-view />
        </div>

        <club-helper />

        <slide-in-from-top>
            <session-import-data key="sessionImportData" v-if="currentlyOpenModal === $options.SESSION_IMPORT_DATA" />
            <import-file-german key="importFileGerman" v-if="currentlyOpenModal === $options.IMPORT_FILE_GERMAN" />
            <add-session-results key="addSessionResults" v-if="currentlyOpenModal === $options.ADD_SESSION_RESULTS" />
            <create-morning-monitoring
                key="createMorningMonitoring"
                v-if="currentlyOpenModal === $options.CREATE_MORNING_MONITORING"
            />
            <add-note key="createMorningMonitoring" v-if="currentlyOpenModal === $options.ADD_NOTE" />
            <join-club key="joinClub" v-if="currentlyOpenModal === $options.JOIN_CLUB" />
        </slide-in-from-top>

        <global-notifications />
        <polling-helper />
        <layout-helper />
    </div>
</template>

<script>
import * as Sentry from '@sentry/browser';
import * as Integrations from '@sentry/integrations';
import 'firebase/messaging';
import CryptoJS from 'crypto-js';

import {
    ADD_NOTE,
    ADD_SESSION_RESULTS,
    CREATE_MORNING_MONITORING,
    IMPORT_FILE_GERMAN,
    JOIN_CLUB,
    SESSION_IMPORT_DATA
} from '@/constants/modals';
import {
    ROUTES_CREATE_CLUB,
    ROUTES_PROFILE_DRV,
    ROUTES_PROFILE_MORNING_MONITORING,
    ROUTES_PROFILE_OVERVIEW,
    ROUTES_SETTINGS_BILLING,
    ROUTES_SETTINGS_BILLING_LICENCE_EXPIRED,
    ROUTES_WELCOME
} from '@/constants/routes';
import { LUDUM_ERROR_CODES } from '@/constants';
import { LudumEventBus, TOGGLE_NAVIGATION } from '@/event-bus';
import { mapGetters } from 'vuex';
import AccountHeader from '@/components/header/AccountHeader.vue';
import ClubHelper from '@/components/helpers/ClubHelper.vue';
import GlobalNotifications from '@/components/global-notifications/GlobalNotifications.vue';
import LayoutHelper from '@/components/helpers/LayoutHelper.vue';
import NavigationMenu from '@/components/navigation/NavigationMenu.vue';
import PollingHelper from '@/components/helpers/PollingHelper.vue';
import permissionMixin from '@/mixins/permissions';
import SelectedClubInfo from '@/components/navigation/SelectedClubInfo.vue';
import SlideInFromTop from '@/components/transitions/SlideInFromTop.vue';
import get from 'get-object-path';
import { Settings } from 'luxon';

// DevExtreme Dictionaries for German and Chinese languages
import deMessages from 'devextreme/localization/messages/de.json';
import zhMessages from 'devextreme/localization/messages/zh.json';
import { locale as devExtremeLocale, loadMessages } from 'devextreme/localization';

export default {
    metaInfo() {
        return {
            changed(info) {
                this.$store.dispatch('setPageTitle', info.title);
            }
        };
    },
    name: 'home',

    ADD_NOTE,
    ADD_SESSION_RESULTS,
    CREATE_MORNING_MONITORING,
    IMPORT_FILE_GERMAN,
    JOIN_CLUB,
    SESSION_IMPORT_DATA,

    components: {
        AccountHeader,
        AddNote: () => import('@/components/add-note/AddNote.vue'),
        AddSessionResults: () => import('@/components/add-session-results/AddSessionResults.vue'),
        ClubHelper,
        CreateMorningMonitoring: () => import('@/components/morning-monitoring/CreateMorningMonitoring.vue'),
        GlobalNotifications,
        ImportFileGerman: () => import('@/components/import-file-german/ImportFileGerman.vue'),
        JoinClub: () => import('@/components/settings/my-clubs/JoinClub.vue'),
        LayoutHelper,
        NavigationMenu,
        PollingHelper,
        SelectedClubInfo,
        SessionImportData: () => import('@/components/session/SessionImportData.vue'),
        SlideInFromTop,
        HeaderProfileCoach: () => import('@/components/header/AccountHeaderProfileCoach.vue')
    },

    mixins: [permissionMixin],

    data() {
        return {
            metaData: {
                title: process.env.VUE_APP_LUDUM_TITLE
            }
        };
    },

    async created() {
        LudumEventBus.$on(TOGGLE_NAVIGATION, () => {
            if (this.$refs.appContent) {
                this.$refs.appContent.classList.toggle('close-navigation');
            }
        });

        this.initLocale();
    },

    mounted() {
        this.$nextTick(() => {
            this.loadGoogleMaps();
            this.loadGoogleTagManager();
            this.initSentry();
            this.saveIntercomSettings();
        });
    },

    destroyed() {
        this.onlineListener && window.removeEventListener('online', this.onlineListener);
        this.offlineListener && window.removeEventListener('offline', this.offlineListener);

        this.removeIntercom();
    },

    watch: {
        selectedClub: {
            immediate: true,
            handler(newClub, oldClub) {
                if (!newClub || (oldClub && newClub && newClub.club_id === oldClub.club_id)) {
                    return;
                }

                this.$store.dispatch('updateErrorCode', null);
                this.$store.dispatch('checkToken').then(response => {
                    const errorCode = get(response, 'response.data.status_code');
                    this.$store.dispatch('updateErrorCode', errorCode);
                    const route = this.hasBillingPermissionInSelectedClub
                        ? ROUTES_SETTINGS_BILLING
                        : ROUTES_SETTINGS_BILLING_LICENCE_EXPIRED;

                    if (errorCode === LUDUM_ERROR_CODES.SUBSCRIPTION_EXPIRED && this.$route.name !== route) {
                        this.$router.push({ name: route });
                    }

                    if (
                        errorCode === LUDUM_ERROR_CODES.NO_CLUBS &&
                        ![ROUTES_WELCOME, ROUTES_CREATE_CLUB].includes(this.$route.name)
                    ) {
                        this.$router.push({ name: ROUTES_WELCOME });
                    }
                });
            }
        },

        clubUsers: {
            immediate: true,
            handler(value) {
                if (value) {
                    this.finishClubSetupNotification();
                }
            }
        }
    },

    methods: {
        initLocale() {
            const locale = Vue.cookie && Vue.cookie.get('language') ? Vue.cookie.get('language') : 'en';
            moment.locale(locale);
            Settings.defaultLocale = locale;
            this.initDevExtremeLocale(locale);
        },

        initDevExtremeLocale(locale) {
            loadMessages(deMessages);
            loadMessages(zhMessages);
            devExtremeLocale(locale);
        },

        initSentry() {
            const { email, id, full_name, is_admin } = this.me;

            if (process.env.VUE_APP_SENTRY_URL) {
                Sentry.init({
                    dsn: process.env.VUE_APP_SENTRY_URL,
                    blacklistUrls: [/logout$/],
                    integrations: [
                        new Integrations.Vue({
                            Vue,
                            attachProps: true
                        })
                    ]
                });
                Sentry.configureScope(scope => {
                    scope.setUser({
                        id,
                        email,
                        full_name,
                        is_admin
                    });
                    scope.setTag('club', `${this.clubId}-${this.selectedClub.club.data.name}`);
                });
            }
        },

        loadGoogleTagManager() {
            this.$loadScript('https://www.googletagmanager.com/gtag/js')
                .then(() => {
                    window.dataLayer = window.dataLayer || [];
                    function gtag() {
                        dataLayer.push(arguments);
                    }
                    window.gtag = gtag;
                    gtag('js', new Date());
                    if (Vue.auth.isAuthenticated()) {
                        gtag('config', 'UA-58100688-4', {
                            user_id: this.me.id
                        });
                    } else {
                        gtag('config', 'UA-58100688-4');
                    }
                })
                .catch(() => {
                    // most probably adblocker is blocking access to gtag manager
                    window.gtag = () => {};
                });
        },

        loadGoogleMaps() {
            this.$store.commit('clubHelper/updateGoogleMaps', false);
            return this.$loadScript(
                `https://maps.googleapis.com/maps/api/js?key=${
                    process.env.VUE_APP_GOOGLE_API_KEY
                }&libraries=places&language=${this.$i18n.locale}`
            )
                .then(() => {
                    // console.log('Google Maps loaded');
                    this.$store.commit('clubHelper/updateGoogleMaps', true);
                })
                .catch(error => {
                    // Failed to fetch script
                    console.log('Failed to load Google Maps', error);
                    this.$store.commit('clubHelper/updateGoogleMaps', false);
                });
        },

        removeIntercom() {
            if (process.env.NODE_ENV === 'production') {
                window.Intercom('shutdown');
            }
        },

        saveIntercomSettings() {
            if (process.env.NODE_ENV === 'production') {
                const intercomSettings = {
                    app_id: process.env.VUE_APP_INTERCOM_APP_ID,
                    name: this.me.full_name,
                    email: this.me.email,
                    user_hash: CryptoJS.enc.Hex.stringify(
                        CryptoJS.HmacSHA256(this.me.email, process.env.VUE_APP_INTERCOM_SECRET_KEY)
                    ),
                    created_at: moment(this.me.created_at).format('X'),
                    role: get(this.selectedClub, 'roles.data[0].name'),
                    all_roles: this.userAllRoles
                };

                intercomSettings.company = {
                    id: get(this.selectedClub, 'club.data.id'),
                    name: get(this.selectedClub, 'club.data.name'),
                    created_at: moment(get(this.selectedClub, 'club.data.created_at')).format('X'),
                    plan: get(this.selectedClub, 'club.data.subscription_info.data.subscription_plan.data.name'),
                    is_individual: get(this.selectedClub, 'club.data.is_individual'),
                    payment_type: get(this.selectedClub, 'club.data.subscription_info.data.pay_via'),
                    creator_email: get(this.selectedClub, 'club.data.creator.data.email')
                };

                window.Intercom('boot', intercomSettings);
            }
        },

        finishClubSetupNotification() {
            const clubUserLength = this.clubUsers.length === 1;

            if (localStorage.getItem('loadFirstTimeSetupNotification') === null) {
                localStorage.setItem('loadFirstTimeSetupNotification', true);
            }

            if (
                this.selectedClub &&
                !this.isSelectedClubIndividual &&
                'ontouchstart' in document.documentElement &&
                clubUserLength &&
                JSON.parse(localStorage.getItem('loadFirstTimeSetupNotification'))
            ) {
                this.$store.dispatch('notifyWarning', {
                    message: this.$t('finishClubSetup'),
                    persist: true
                });

                localStorage.setItem('loadFirstTimeSetupNotification', false);
            }
        }
    },

    computed: {
        ...mapGetters({
            currentlyOpenModal: 'currentlyOpenModal',
            hasBillingPermissionInSelectedClub: 'hasBillingPermissionInSelectedClub',
            isSelectedClubIndividual: 'isSelectedClubIndividual',
            me: 'clubHelper/me',
            pageTitle: 'pageTitle',
            selectedClub: 'selectedClub',
            userAllRoles: 'userAllRoles',
            clubUsers: 'clubHelper/clubUsers',
            clubId: 'clubId'
        }),

        formattedTitle() {
            return this.pageTitle.split(`- ${process.env.VUE_APP_LUDUM_TITLE}`)[0];
        },

        shouldSeeAthleteDropdown() {
            return this.isOnProfilePage && this.canViewAthleteProfile() && this.profileId !== this.me.id;
        },

        isOnProfilePage() {
            return [ROUTES_PROFILE_DRV, ROUTES_PROFILE_MORNING_MONITORING, ROUTES_PROFILE_OVERVIEW].includes(
                this.$route.name
            );
        },

        profileId() {
            return Number(this.$route.params.id ? this.$route.params.id : this.me.id);
        }
    }
};
</script>
